<template>
  <div class="bg-black/20 rounded-lg overflow-hidden">
    <!-- Row Header -->
    <div class="bg-black/40 px-4 py-2 flex justify-between items-center">
      <h3 class="text-sm font-medium text-white">Monitor #{{ rowId }}</h3>
      <button
        v-if="canRemove"
        @click="$emit('remove-row', rowId)"
        class="text-red-400 hover:text-red-300 transition-colors p-1 rounded-sm hover:bg-red-900/20"
        title="Remove Monitor "
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- Main Content: Source List + Player -->
    <div class="flex h-96">
      <!-- Left Side: Source List (50% width) -->
      <div class="w-1/2">
        <MonitorSourceList
          :row-id="rowId"
          :input-sources="inputSources"
          :selected-source="selectedSource"
          :active-connection="activeConnection"
          :thumbnail-provider="thumbnailProvider"
          @source-selected="handleSourceSelected"
          @audio-channel-selected="handleAudioChannelSelected"
        />
      </div>

      <!-- Right Side: Player (50% width) -->
      <div class="w-1/2">
        <div class="h-full bg-black overflow-hidden relative">
          <MvPlayerWebRtc
            v-if="playerSource"
            :show-close-button="false"
            :show-source-control="false"
            :initial-source="playerSource"
            :initial-source-name="playerSourceName"
            :initial-audio-state="{
              selectedAudioPair: getAudioPairId(activeConnection ? activeConnection.audioChannel : ''),
              volume: 50,
              leftChannelMuted: false,
              rightChannelMuted: false,
              isMuted: false
            }"
            :additional-sources="[]"
            :key="playerKey"
            ref="playerRef"
          />
          
          <!-- Empty Player State -->
          <div v-else class="h-full flex items-center justify-center text-gray-500">
            <div class="text-center">
              <svg class="mx-auto h-12 w-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <p class="text-sm">Select a source to start monitoring</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Connection Status Bar -->
    <div v-if="activeConnection" class="bg-orange-500/20 px-4 py-2">
      <div class="flex items-center justify-between text-xs">
        <div class="flex items-center gap-2 text-orange-300">
          <div class="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
          <span>Connected to: <strong>{{ activeConnection.sourceName }}</strong></span>
          <span v-if="activeConnection.audioChannel">
            (Audio: {{ activeConnection.audioChannel }})
          </span>
        </div>
        <button
          @click="disconnectSource"
          class="text-orange-300 hover:text-orange-200 transition-colors underline"
        >
          Disconnect
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import MonitorSourceList from "./MonitorSourceList.vue";
import MvPlayerWebRtc from "@/components/MvPlayerWebRtc.vue";

// Props
const props = defineProps({
  rowId: {
    type: Number,
    required: true
  },
  inputSources: {
    type: Array,
    default: () => []
  },
  canRemove: {
    type: Boolean,
    default: false
  },
  thumbnailProvider: {
    type: Object,
    default: () => ({})
  }
});

// Emits
const emit = defineEmits(['remove-row']);

// Reactive state
const selectedSource = ref(null);
const playerSource = ref("");
const playerSourceName = ref(""); // Store source name separately
const playerKey = ref(0); // For forcing player remount
const activeConnection = ref(null);
const playerRef = ref(null);

// Handle source selection from the source list
const handleSourceSelected = (source) => {
  console.log(`Row ${props.rowId}: Source selected:`, source);
  selectedSource.value = source;
  
  // Don't automatically connect - wait for audio channel selection
  // This allows the dropdown to show
};

// Handle audio channel selection (this will trigger the connection)
const handleAudioChannelSelected = (source, audioChannel) => {
  console.log(`Row ${props.rowId}: Audio channel selected:`, source, audioChannel);
  
  // Set the player source to connect
  playerSource.value = source.url;
  playerSourceName.value = source.name; // Store the source name
  playerKey.value++; // Force remount of player
  
  // Track the active connection for status display
  activeConnection.value = {
    sourceId: source.id,
    sourceName: source.name,
    audioChannel: audioChannel,
    url: source.url
  };
  
  console.log(`Row ${props.rowId}: Connected to ${source.name} with audio channel ${audioChannel}`);
};

// Disconnect current source
const disconnectSource = () => {
  console.log(`Row ${props.rowId}: Disconnecting source`);
  
  selectedSource.value = null;
  playerSource.value = "";
  playerSourceName.value = ""; // Clear source name
  activeConnection.value = null;
  playerKey.value++;
};

// Helper to extract audio pair ID from string
const getAudioPairId = (channelString) => {
  if (!channelString) return 1;
  const match = channelString.match(/Pair (\d+)/);
  return match ? parseInt(match[1]) : 1;
};

// Watch for changes in activeConnection to handle UI updates
watch(activeConnection, (newConnection, oldConnection) => {
  if (newConnection && !oldConnection) {
    console.log(`Row ${props.rowId}: Connection established`);
  } else if (!newConnection && oldConnection) {
    console.log(`Row ${props.rowId}: Connection terminated`);
  }
});
</script>

<style scoped>
/* Component-specific styles */
.monitor-row-container {
  transition: all 0.2s ease-in-out;
}

/* Connection status animation */
@keyframes connectionPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.animate-pulse {
  animation: connectionPulse 2s infinite;
}
</style>